import { useMemo, useState } from 'react';
import { Badge } from '@/components/ui/Badge';
import type { BadgeTone } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Pagination } from '@/components/ui/Pagination';
import { SearchInput } from '@/components/ui/SearchInput';
import { Select } from '@/components/ui/Select';
import { useTranslation } from '@/i18n';
import { cx } from '@/lib/cx';
import {
  CHANGEABLE_FIELDS,
  EVENT_TYPES,
  fetchEvent,
  fetchMessage,
  FLIGHT_LEGS,
} from './api';
import type { EventCriteria, EventType, IntegrationEventInfo } from './api';
import { fieldLabel, summarise } from './changes';
import { ChangeList, ChangeSummary, LazyPayload } from './parts';
import { apiTimestamp, DAY_RANGES, ROWS_OPTIONS, useMonitorParams } from './useMonitorParams';
import type { DayRange } from './useMonitorParams';
import { useEvents, useSourceCodes } from './useMonitor';
import styles from './MonitorPage.module.css';

/**
 * The event journal: what the feeds did to the schedule, and what went wrong.
 *
 * Unfiltered it shows the server's default window — the **last 48 hours** — while a
 * flight number or a row id reaches all the way back, so the note above the table
 * says which of the two the reader is looking at. The count can arrive capped at
 * 10 000; the pager renders it as "10 000+" and trusts `lastBlock` alone.
 */
export const EventsView = () => {
  const { params, update, reset, hasFilters } = useMonitorParams();
  const sourceCodes = useSourceCodes();
  const { t } = useTranslation();

  const criteria = useMemo<EventCriteria>(
    () => ({
      sourceCode: params.source,
      eventType: params.type,
      flightType: params.leg,
      changedField: params.field,
      flightNumber: params.flight,
      ...(params.scheduleId === null ? {} : { airportScheduleId: params.scheduleId }),
      // Resolved here rather than stored, so `days=7` still means the last seven
      // days when the link is opened tomorrow. Absent leaves the server's own
      // 48-hour window in place.
      ...(params.days === null ? {} : { createdFrom: daysAgo(params.days) }),
      pageNum: params.page,
      rows: params.rows,
    }),
    [
      params.source,
      params.type,
      params.leg,
      params.field,
      params.flight,
      params.scheduleId,
      params.days,
      params.page,
      params.rows,
    ],
  );

  const { rows, currPage, total, capped, isLastPage, isLoading, errorKey } = useEvents(criteria);

  /** Ephemeral, so it is state rather than URL: an expanded row is not a place. */
  const [expandedId, setExpandedId] = useState<number | null>(null);

  /** A flight or a row reaches back years; anything else is the server's 48 hours. */
  const isTargeted = params.flight !== '' || params.scheduleId !== null;

  /**
   * The dropdown's options, from the server's own source list.
   *
   * The current value is folded in even when the list does not contain it — the
   * codes arrive a moment after the first paint, and a native select whose value is
   * not among its options renders BLANK, so a filtered feed would briefly look
   * unfiltered. It also keeps a stale bookmarked code visible rather than silently
   * showing the reader a filter they cannot see.
   */
  const sourceOptions = useMemo(() => {
    const codes = params.source !== '' && !sourceCodes.includes(params.source)
      ? [params.source, ...sourceCodes]
      : sourceCodes;

    return codes.map((code) => ({ value: code, label: code }));
  }, [sourceCodes, params.source]);

  return (
    <div>
      <div className={styles.filters}>
        <label className={styles.filter}>
          <span className={styles.filterLabel}>{t('monitor.sources.columns.source')}</span>
          <Select
            options={sourceOptions}
            placeholder={t('monitor.events.allSources')}
            value={params.source}
            active={params.source !== ''}
            onChange={(event) => update({ source: event.target.value })}
            aria-label={t('monitor.sources.columns.source')}
          />
        </label>

        <label className={styles.filter}>
          <span className={styles.filterLabel}>{t('monitor.events.columns.type')}</span>
          <Select
            options={EVENT_TYPES.map((type) => ({
              value: type,
              label: t(`monitor.events.types.${type}`),
            }))}
            placeholder={t('monitor.events.allTypes')}
            value={params.type}
            active={params.type !== ''}
            onChange={(event) => update({ type: event.target.value as typeof params.type })}
            aria-label={t('monitor.events.columns.type')}
          />
        </label>

        {/* The placeholder is an EXAMPLE, not the label again. The label already sits
            above the field, so repeating it there was both redundant and — in a box
            sized for a code — clipped halfway through the word. */}
        <SearchInput
          label={t('monitor.events.flightLabel')}
          placeholder={t('monitor.events.flightExample')}
          value={params.flight}
          onChange={(flight) => update({ flight })}
          upper
          width="sm"
        />

        {/*
          A filter, not a button hidden inside a row. It is the same question as the
          flight number — "show me everything about this one thing" — so it belongs
          beside it, where it can be seen, typed into and cleared.
        */}
        <SearchInput
          label={t('monitor.events.rowLabel')}
          placeholder={t('monitor.events.rowExample')}
          value={params.scheduleId === null ? '' : String(params.scheduleId)}
          onChange={(value) => update({ scheduleId: scheduleIdFrom(value) })}
          width="sm"
        />

        <label className={styles.filter}>
          <span className={styles.filterLabel}>{t('schedule.columns.leg')}</span>
          <Select
            options={FLIGHT_LEGS.map((leg) => ({
              value: leg,
              label: t(`schedule.types.${leg}`),
            }))}
            placeholder={t('monitor.events.allLegs')}
            value={params.leg}
            active={params.leg !== ''}
            onChange={(event) => update({ leg: event.target.value as typeof params.leg })}
            aria-label={t('schedule.columns.leg')}
          />
        </label>

        {/* Which column moved — the question the change feed is usually opened to
            answer ("who keeps rewriting the estimate?"). */}
        <label className={styles.filter}>
          <span className={styles.filterLabel}>{t('monitor.events.fieldLabel')}</span>
          <Select
            options={CHANGEABLE_FIELDS.map((field) => ({
              value: field,
              label: fieldLabel(field, t),
            }))}
            placeholder={t('monitor.events.allFields')}
            value={params.field}
            active={params.field !== ''}
            onChange={(event) => update({ field: event.target.value as typeof params.field })}
            aria-label={t('monitor.events.fieldLabel')}
          />
        </label>

        {/* The server windows to 48 hours unless asked otherwise; this is the ask. */}
        <label className={styles.filter}>
          <span className={styles.filterLabel}>{t('monitor.events.periodLabel')}</span>
          <Select
            options={DAY_RANGES.map((days) => ({
              value: String(days),
              label: t('monitor.events.lastDays', { count: days }),
            }))}
            placeholder={t('monitor.events.defaultWindow')}
            value={params.days === null ? '' : String(params.days)}
            active={params.days !== null}
            onChange={(event) =>
              update({
                days: event.target.value === '' ? null : (Number(event.target.value) as DayRange),
              })
            }
            aria-label={t('monitor.events.periodLabel')}
          />
        </label>

        <Button size="sm" onClick={reset} disabled={!hasFilters}>
          {t('monitor.clearFilters')}
        </Button>
      </div>

      {/*
        Which window this is. The two targeted filters are never windowed by the
        server, so saying which of the two the reader is looking at is the difference
        between "there is nothing" and "there is nothing in the last two days".
      */}
      <p className={styles.note}>
        {t(isTargeted ? 'monitor.events.fullHistory' : 'monitor.events.window')}
      </p>

      {errorKey && (
        <p className={styles.error} role="alert">
          {t(errorKey)}
        </p>
      )}

      <div className={styles.tableWrap}>
        {isLoading && <div className={styles.loadingBar} aria-hidden="true" />}

        <table className={cx(styles.table, isLoading && styles.loading)}>
          <thead>
            <tr>
              <th>{t('monitor.events.columns.time')}</th>
              <th>{t('monitor.sources.columns.source')}</th>
              <th>{t('monitor.events.columns.type')}</th>
              <th>{t('monitor.events.columns.flight')}</th>
              <th>{t('monitor.events.columns.details')}</th>
              <th>{t('monitor.events.columns.payload')}</th>
            </tr>
          </thead>

          <tbody>
            {rows.length === 0 && !isLoading ? (
              <tr>
                <td colSpan={6} className={styles.message}>
                  {t('monitor.events.nothingFound')}
                </td>
              </tr>
            ) : (
              rows.map((event) => (
                <EventRow
                  key={event.id}
                  event={event}
                  isExpanded={expandedId === event.id}
                  onToggle={() =>
                    setExpandedId((current) => (current === event.id ? null : event.id))
                  }
                />
              ))
            )}
          </tbody>
        </table>
      </div>

      <Pagination
        page={currPage}
        rows={params.rows}
        total={total}
        capped={capped}
        countLabel={t('monitor.events.found')}
        isLastPage={isLastPage}
        rowsOptions={ROWS_OPTIONS}
        onPage={(page) => update({ page })}
        onRows={(rowCount) => update({ rows: rowCount })}
        isLoading={isLoading}
      />
    </div>
  );
};

/**
 * `createdFrom` for a period expressed in days.
 *
 * Called from a `useMemo` on the params, so it runs once per URL rather than once
 * per render — resolving `new Date()` during every render would change the criteria
 * every render, which is how a request loop starts.
 */
const daysAgo = (days: number): string => {
  const from = new Date();
  from.setDate(from.getDate() - days);
  return apiTimestamp(from);
};

/**
 * The typed row id, or nothing.
 *
 * Anything that is not a positive whole number clears the filter rather than being
 * sent: the id addresses a database row, and a half-typed or mistyped one would
 * quietly answer with somebody else's history.
 */
const scheduleIdFrom = (value: string): number | null => {
  const trimmed = value.trim();
  if (!/^\d+$/.test(trimmed)) return null;

  const id = Number(trimmed);
  return Number.isSafeInteger(id) && id > 0 ? id : null;
};

const EVENT_TONE: Record<EventType, BadgeTone> = {
  CREATED: 'success',
  UPDATED: 'accent',
  DELETED: 'neutral',
  RECEIVED: 'info',
  WARNING: 'warning',
  ERROR: 'danger',
};

/**
 * One event, expandable in place.
 *
 * Expansion rather than a side panel: the journal is read many rows at a time, and a
 * panel showing one event while the eye is on the next costs more than it gives.
 * The payloads load only inside an expanded row — see `LazyPayload`.
 */
const EventRow = ({
  event,
  isExpanded,
  onToggle,
}: {
  event: IntegrationEventInfo;
  isExpanded: boolean;
  onToggle: () => void;
}) => {
  const { t, formatDateTime } = useTranslation();

  const changes = event.changes ?? [];
  // Destructured so the null check below narrows a type that survives into the
  // callback — a property access narrows only until the next closure.
  const { messageId } = event;

  /**
   * Whether opening this row shows anything beyond the diff.
   *
   * The server's own flag plus the presence of a delivery — never the nullness of
   * `rawData`, which is null on every list row regardless.
   */
  const hasPayload = event.hasRawData || messageId !== null;

  return (
    <>
      <tr className={cx(styles.row, isExpanded && styles.selected)} onClick={onToggle}>
        <td className={styles.timeCell}>{formatDateTime(event.created)}</td>

        <td className={styles.mono}>
          {event.sourceCode ?? <span className={styles.empty}>{t('monitor.events.noSource')}</span>}
        </td>

        <td>
          <Badge tone={EVENT_TONE[event.eventType]}>
            {t(`monitor.events.types.${event.eventType}`)}
          </Badge>
        </td>

        <td>
          {event.flightNumber ? (
            <>
              <span className={styles.mono}>{event.flightNumber}</span>
              {event.airportIataCode && <span className={styles.subCell}> {event.airportIataCode}</span>}
              {event.flightType && (
                <span className={styles.subCell}> · {t(`schedule.types.${event.flightType}`)}</span>
              )}
            </>
          ) : (
            <span className={styles.empty}>—</span>
          )}
        </td>

        {/*
          The humanised diff where there is one, the server's own sentence where
          there is not. `details` carries the same diff as raw text — camelCase
          names, the word "null", literal arrows — so showing it beside `changes`
          would be the same fact told twice, the worse way.
        */}
        {/*
          The structured diff where there is one. `details` only appears where there
          is NOT — a received telex, a warning, an exception — because those describe
          a message rather than a flight and have no fields to show. Where both exist
          they are the same fact, and `details` is the worse telling of it: camelCase
          names, the word "null", literal arrows.

          The full text is the tooltip, since one line of a table cannot hold five
          changed fields.
        */}
        <td className={styles.detailsCell} title={changes.length > 0 ? summarise(changes, t) : (event.details ?? undefined)}>
          {changes.length > 0 ? (
            <ChangeSummary changes={changes} />
          ) : (
            (event.details ?? <span className={styles.empty}>—</span>)
          )}
        </td>

        {/* Says whether there is anything behind this row before it is opened. The
            row itself is the control — this only reports. */}
        <td className={styles.payloadCell}>
          {hasPayload ? (
            <span className={styles.payloadLink}>
              {t(isExpanded ? 'monitor.events.hide' : 'monitor.events.show')}
            </span>
          ) : (
            <span className={styles.empty}>—</span>
          )}
        </td>
      </tr>

      {isExpanded && (
        <tr className={styles.expandRow}>
          <td colSpan={6}>
            <div className={styles.expanded}>
              {changes.length > 0 && <ChangeList changes={changes} />}

              {/* No diff to render: an exception, a message summary, a warning. It
                  is preformatted because a stack trace is. */}
              {changes.length === 0 && event.details && (
                <pre className={styles.detailsFull}>{event.details}</pre>
              )}

              {/*
                Two different answers, and both are worth having:

                  rawData    — what the source said about THIS flight
                  rawPayload — the whole delivery it arrived in

                For an airport snapshot the first is a `<FLIGHT>` element and the
                second does not exist at all, because a successful poll deliberately
                keeps no 1.5 MB file. For a telex they are the same text. So the
                per-flight record comes first, and the delivery is offered under its
                own heading rather than instead.

                Both are gated on the server's own flag, never on the field being
                null: the list does not select those columns, so null there means
                "not on this page" and nothing more. Guessing otherwise is what made
                a working feed look like it had lost data.
              */}
              {event.hasRawData && (
                <section className={styles.rawBlock}>
                  <h4 className={styles.rawTitle}>{t('monitor.events.sourceRecord')}</h4>
                  <LazyPayload
                    emptyKey="monitor.payloadEmpty"
                    load={() => fetchEvent(event.id).then((full) => full.rawData)}
                  />
                </section>
              )}

              {messageId !== null && (
                <section className={styles.rawBlock}>
                  <h4 className={styles.rawTitle}>{t('monitor.events.wholeDelivery')}</h4>
                  <LazyPayload
                    emptyKey="monitor.deliveryNoContent"
                    load={() => fetchMessage(messageId).then((message) => message.rawPayload)}
                  />
                </section>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
};
